import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, MessageCircle, CheckCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { differenceInMinutes, differenceInHours, format } from "date-fns";

interface ResponseMetrics {
  avgFirstResponseMinutes: number;
  avgResolutionMinutes: number;
  totalResolved: number;
  totalConversations: number;
  respondedWithin1Hour: number;
  resolvedWithin24Hours: number;
}

export function SupportResponseTimeAnalytics() {
  const [metrics, setMetrics] = useState<ResponseMetrics>({
    avgFirstResponseMinutes: 0,
    avgResolutionMinutes: 0,
    totalResolved: 0,
    totalConversations: 0,
    respondedWithin1Hour: 0,
    resolvedWithin24Hours: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      // Fetch all support conversations
      const { data: conversations, error: convError } = await supabase
        .from("conversations")
        .select("id, created_at, status, created_by, updated_at")
        .in("type", ["support", "claim_inquiry"]);

      if (convError) throw convError;

      if (!conversations || conversations.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch all messages for these conversations
      const conversationIds = conversations.map(c => c.id);
      const { data: messages, error: msgError } = await supabase
        .from("messages")
        .select("id, conversation_id, sender_id, created_at")
        .in("conversation_id", conversationIds)
        .order("created_at", { ascending: true });

      if (msgError) throw msgError;

      // Calculate metrics
      let totalFirstResponseTime = 0;
      let firstResponseCount = 0;
      let respondedWithin1Hour = 0;
      
      let totalResolutionTime = 0;
      let resolvedCount = 0;
      let resolvedWithin24Hours = 0;

      for (const conv of conversations) {
        // Find first admin response (message not from conversation creator)
        const convMessages = (messages || []).filter(m => m.conversation_id === conv.id);
        const firstAdminMessage = convMessages.find(m => m.sender_id !== conv.created_by);

        if (firstAdminMessage) {
          const responseMinutes = differenceInMinutes(
            new Date(firstAdminMessage.created_at),
            new Date(conv.created_at)
          );
          totalFirstResponseTime += responseMinutes;
          firstResponseCount++;
          
          if (responseMinutes <= 60) {
            respondedWithin1Hour++;
          }
        }

        // Calculate resolution time for resolved/closed conversations
        if (conv.status === "resolved" || conv.status === "closed") {
          const resolutionMinutes = differenceInMinutes(
            new Date(conv.updated_at),
            new Date(conv.created_at)
          );
          totalResolutionTime += resolutionMinutes;
          resolvedCount++;
          
          if (resolutionMinutes <= 24 * 60) {
            resolvedWithin24Hours++;
          }
        }
      }

      setMetrics({
        avgFirstResponseMinutes: firstResponseCount > 0 ? Math.round(totalFirstResponseTime / firstResponseCount) : 0,
        avgResolutionMinutes: resolvedCount > 0 ? Math.round(totalResolutionTime / resolvedCount) : 0,
        totalResolved: resolvedCount,
        totalConversations: conversations.length,
        respondedWithin1Hour,
        resolvedWithin24Hours: resolvedCount > 0 ? Math.round((resolvedWithin24Hours / resolvedCount) * 100) : 0,
      });
    } catch (error) {
      console.error("Error fetching response metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 24 * 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / (24 * 60));
      const hours = Math.floor((minutes % (24 * 60)) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  const getResponseTimeIndicator = (minutes: number) => {
    if (minutes <= 30) return { icon: TrendingUp, color: "text-green-500", label: "Excellent" };
    if (minutes <= 60) return { icon: Minus, color: "text-yellow-500", label: "Good" };
    return { icon: TrendingDown, color: "text-red-500", label: "Needs Improvement" };
  };

  const getResolutionTimeIndicator = (minutes: number) => {
    if (minutes <= 4 * 60) return { icon: TrendingUp, color: "text-green-500", label: "Excellent" };
    if (minutes <= 24 * 60) return { icon: Minus, color: "text-yellow-500", label: "Good" };
    return { icon: TrendingDown, color: "text-red-500", label: "Needs Improvement" };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Response Time Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const responseIndicator = getResponseTimeIndicator(metrics.avgFirstResponseMinutes);
  const resolutionIndicator = getResolutionTimeIndicator(metrics.avgResolutionMinutes);
  const ResponseIcon = responseIndicator.icon;
  const ResolutionIcon = resolutionIndicator.icon;

  const responseRate = metrics.totalConversations > 0 
    ? Math.round((metrics.respondedWithin1Hour / metrics.totalConversations) * 100) 
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Response Time Analytics
        </CardTitle>
        <CardDescription>
          Support request response and resolution metrics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* First Response Time */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Avg. First Response</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {metrics.avgFirstResponseMinutes > 0 ? formatTime(metrics.avgFirstResponseMinutes) : "N/A"}
              </span>
              {metrics.avgFirstResponseMinutes > 0 && (
                <ResponseIcon className={`h-4 w-4 ${responseIndicator.color}`} />
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Target: &lt; 1 hour</span>
            <span className={responseIndicator.color}>{responseIndicator.label}</span>
          </div>
          <Progress value={Math.min(100, 100 - (metrics.avgFirstResponseMinutes / 60) * 100)} className="h-2" />
        </div>

        {/* Resolution Time */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Avg. Resolution Time</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {metrics.avgResolutionMinutes > 0 ? formatTime(metrics.avgResolutionMinutes) : "N/A"}
              </span>
              {metrics.avgResolutionMinutes > 0 && (
                <ResolutionIcon className={`h-4 w-4 ${resolutionIndicator.color}`} />
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Target: &lt; 24 hours</span>
            <span className={resolutionIndicator.color}>{resolutionIndicator.label}</span>
          </div>
          <Progress value={Math.min(100, 100 - (metrics.avgResolutionMinutes / (24 * 60)) * 100)} className="h-2" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{responseRate}%</p>
            <p className="text-xs text-muted-foreground">Responded within 1hr</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{metrics.resolvedWithin24Hours}%</p>
            <p className="text-xs text-muted-foreground">Resolved within 24hrs</p>
          </div>
        </div>

        {/* Summary */}
        <div className="text-xs text-muted-foreground text-center pt-2 border-t">
          Based on {metrics.totalConversations} total conversations • {metrics.totalResolved} resolved
        </div>
      </CardContent>
    </Card>
  );
}
